/**
 * SpawnPicker — first screen the player sees.
 *
 * Layout: full-screen overlay with the Virtuaroo title, a name input, hat
 * color swatches, a minimap-style farm diagram, and a list of top spawn
 * locations. Click a location to drop in.
 */

import { html, render } from 'lit-html';
import { useUI } from '@/state/store';
import { loadIdentity, saveIdentity } from '@/player/Identity';
import { SPAWN_POINTS, ZONES, type ZoneDef } from '@shared/zones';

const HAT_COLORS = [
  '#ff5f6d',
  '#ffc371',
  '#7ce0a3',
  '#56c2ff',
  '#bd72ff',
  '#ff8ec0',
  '#f5f0e8',
  '#3a2a44'
];

export interface SpawnPickerCallbacks {
  onSpawn: (zoneId: string, name: string, hatColor: number) => void;
}

export class SpawnPicker {
  private container: HTMLDivElement | null = null;
  private name: string;
  private hatColor: number;

  constructor(private cb: SpawnPickerCallbacks) {
    const id = loadIdentity();
    this.name = id.displayName;
    this.hatColor = id.hatColor;
  }

  mount(): void {
    if (this.container) return;
    const root = document.getElementById('ui-root');
    if (!root) return;
    this.container = document.createElement('div');
    this.container.className = 'spawn-picker';
    root.appendChild(this.container);
    this.render();
  }

  unmount(): void {
    if (!this.container) return;
    this.container.remove();
    this.container = null;
  }

  private spawn(zoneId: string): void {
    if (!this.name.trim()) return;
    saveIdentity({ displayName: this.name.trim(), hatColor: this.hatColor });
    useUI.getState().setIdentity({ displayName: this.name.trim(), hatColor: this.hatColor });
    useUI.getState().closeSpawnPicker();
    this.cb.onSpawn(zoneId, this.name.trim(), this.hatColor);
    this.unmount();
  }

  private render(): void {
    if (!this.container) return;

    const tpl = html`
      <div class="sp-bg"></div>
      <div class="sp-card">
        <div class="sp-title">VIRTUAROO</div>
        <div class="sp-sub">A virtual hangout for Bonnaroovians.</div>

        <div class="sp-row">
          <label>Display name</label>
          <input
            class="sp-input"
            type="text"
            maxlength="24"
            .value=${this.name}
            @input=${(e: Event) => {
              this.name = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Enter') this.spawn('arch');
            }}
          />
        </div>

        <div class="sp-row">
          <label>Hat color</label>
          <div class="sp-hats">
            ${HAT_COLORS.map(
              (c, i) => html`
                <button
                  class="sp-hat ${i === this.hatColor ? 'on' : ''}"
                  style="background:${c}"
                  @click=${() => {
                    this.hatColor = i;
                    this.render();
                  }}
                ></button>
              `
            )}
          </div>
        </div>

        <div class="sp-row">
          <label>Where to drop in</label>
          <div class="sp-spawn-grid">
            ${SPAWN_POINTS.map(
              (zone: ZoneDef) => html`
                <button class="sp-spawn" @click=${() => this.spawn(zone.id)}>
                  <div class="sp-spawn-name">${zone.label}</div>
                  <div class="sp-spawn-vibe">${zone.vibe ?? ''}</div>
                </button>
              `
            )}
          </div>
        </div>

        <div class="sp-map-row">
          <svg class="sp-map" viewBox="-500 -500 1000 1000">
            ${Object.values(ZONES).map((z) => {
              const r = z.radius * 0.7;
              return html`
                <circle
                  cx="${z.center[0]}"
                  cy="${z.center[1]}"
                  r="${r}"
                  fill="rgba(255,195,113,0.07)"
                  stroke="rgba(255,195,113,0.35)"
                  stroke-width="3"
                />
                ${z.spawnable
                  ? html`<text x="${z.center[0]}" y="${z.center[1]}" text-anchor="middle" fill="#ffc371" font-size="22">${z.label}</text>`
                  : html`<text x="${z.center[0]}" y="${z.center[1]}" text-anchor="middle" fill="#888" font-size="18">${z.label}</text>`}
              `;
            })}
          </svg>
        </div>

        <div class="sp-footer">
          Press <kbd>WASD</kbd> to walk, <kbd>Shift</kbd> to run, <kbd>T</kbd> to chat,
          <kbd>1–9</kbd> to emote, <kbd>V</kbd> to switch camera, <kbd>M</kbd> to reopen this map.
        </div>
      </div>
    `;
    render(tpl, this.container);
  }
}
